import {BaseProps} from '@treatwell/ui';
import clsx from 'clsx';
import {lazy, Suspense} from 'react';
import {FixedFooter} from '../components/Footer/FixedFooter';
import {FixedFooterDeposits} from '../components/Footer/FixedFooterDeposits';
import {Widget} from '../components/Widget';
import styles from './Layout.module.css';

const defaultTag = 'div';
type AllowedTags = typeof defaultTag | 'section' | 'header' | 'aside';

type LayoutProps<T extends AllowedTags = typeof defaultTag> = {
  children: React.ReactNode;
  inline?: boolean;
  marginBottom?: boolean;
} & BaseProps<T>;

const WidgetHeader = lazy(
  () => import('../components/WidgetHeader/WidgetHeaderContainer'),
);

export const PageLayout = ({
  children,
  showFixedFooter = false,
  showFixedFooterDeposits = false,
  isOnMultiStaffSelection = false,
}: {
  children: React.ReactElement | React.ReactElement[];
  showFixedFooter?: boolean;
  showFixedFooterDeposits?: boolean;
  isOnMultiStaffSelection?: boolean;
  isWidget?: boolean;
}) => {
  return (
    <div className={styles.root}>
      <Widget>
        <Suspense>
          <WidgetHeader />
        </Suspense>
      </Widget>

      <main
        aria-live="polite"
        className={clsx(styles.main, styles.marginBottomWithoutFixedFooter, {
          [styles.marginBottom]: showFixedFooter,
        })}
        data-cy="AppMain"
      >
        <div className="AppContainer">{children}</div>
      </main>

      {showFixedFooter && (
        <FixedFooter isOnMultiStaffSelection={isOnMultiStaffSelection} />
      )}
      {showFixedFooterDeposits && <FixedFooterDeposits />}
    </div>
  );
};

export const GridLayout = ({
  children,
  isOnMultiStaffSelection = false,
}: {
  isOnMultiStaffSelection?: boolean;
  children: React.ReactElement[] | React.ReactElement;
}) => {
  return (
    <div
      className={clsx(styles.layout, styles.wideLayout, {
        [styles.isMultiStaffEnabledLayout]: isOnMultiStaffSelection,
      })}
    >
      {children}
    </div>
  );
};

export const PageContainer = ({
  children,
  fixedHeight,
  withPadding,
}: {
  children: React.ReactElement | React.ReactElement[];
  fixedHeight?: boolean;
  withPadding?: boolean;
}) => {
  return (
    <div
      className={clsx(styles.container, {
        [styles.fixedHeight]: fixedHeight,
        [styles.layoutContainer]: !withPadding,
      })}
    >
      {children}
    </div>
  );
};

export const GridLayoutSide = ({
  children,
  inline = false,
}: {
  children: React.ReactElement;
  inline?: boolean;
}) => {
  return (
    <div
      className={clsx(styles.sideContent, {
        [styles.inline]: inline,
      })}
    >
      {children}
    </div>
  );
};

export const GridLayoutContent = <T extends AllowedTags>({
  children,
  inline = false,
  ...restProps
}: LayoutProps<T>) => {
  return (
    <div
      className={clsx(styles.mainContent, {
        [styles.inline]: inline,
      })}
      {...restProps}
    >
      {children}
    </div>
  );
};
